import React, { useMemo, useState } from 'react';
import './Login.css';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const { toast } = useToast();

  const { signUp, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const resolvedRole = useMemo(() => {
    const roleParam = (searchParams.get('role') || '').toLowerCase();
    if (roleParam === 'merchant' || roleParam === 'courier') {
      return roleParam;
    }
    return 'user';
  }, [searchParams]);

  const roleCopy = {
    user: {
      subtitle: 'Crie sua conta para comecar',
      badge: 'Cadastro de cliente',
    },
    merchant: {
      subtitle: 'Cadastre sua loja para vender',
      badge: 'Cadastro de parceiro (lojista)',
    },
    courier: {
      subtitle: 'Cadastre-se para fazer entregas',
      badge: 'Cadastro de entregador (motoboy)',
    },
  };

  const currentRoleCopy = roleCopy[resolvedRole] || roleCopy.user;

  function validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  function validatePassword(password) {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar,
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChar
    };
  }

  function validateForm() {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!validateEmail(email)) {
      newErrors.email = 'E-mail inválido';
    }

    const passwordValidation = validatePassword(password);
    if (!password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (!passwordValidation.isValid) {
      const missingRequirements = [];
      if (!passwordValidation.minLength) missingRequirements.push('mínimo 8 caracteres');
      if (!passwordValidation.hasUpperCase) missingRequirements.push('letra maiúscula');
      if (!passwordValidation.hasLowerCase) missingRequirements.push('letra minúscula');
      if (!passwordValidation.hasNumber) missingRequirements.push('número');
      if (!passwordValidation.hasSpecialChar) missingRequirements.push('caractere especial');
      
      newErrors.password = `Senha deve conter: ${missingRequirements.join(', ')}`;
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirme sua senha';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (isSubmitting) return;
    
    const isValid = validateForm();
    
    if (!isValid) {
      const errorMessages = Object.values(errors).filter(Boolean);
      toast({
        variant: "destructive",
        title: "Erro de Validação",
        description: errorMessages[0] || "Por favor, corrija os erros no formulário.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await signUp(email, password, name, resolvedRole);
      
      toast({
        title: "Sucesso!",
        description: "Cadastro realizado. Verifique seu email para confirmar a conta antes de entrar.",
      });

      setTimeout(() => {
        const loginParams = new URLSearchParams({
          confirmEmail: '1',
          email: email.trim(),
        });
        navigate(`/login?${loginParams.toString()}`, {
          replace: true,
          state: {
            pendingEmailConfirmation: true,
            email: email.trim(),
          },
        });
      }, 2000);
      
    } catch (err) {
      if (err?.name === 'AbortError') {
        return;
      }
      
      toast({
        variant: "destructive",
        title: "Erro no Cadastro",
        description: (err?.getUserFriendlyMessage?.() || err?.message || "Ocorreu um erro ao criar sua conta."),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="login-container">
      <div className="login-box auth-card shadow-2xl border border-gray-100"> 
        <h1 className="login-title">CorreFood</h1>
        <p className="login-subtitle">{currentRoleCopy.subtitle}</p>
        <p className="auth-role-badge text-sm text-gray-500 mt-1">{currentRoleCopy.badge}</p>

        <form onSubmit={handleSubmit} className="auth-form space-y-4">
          <div>
            <input
              className={`auth-input w-full p-3 rounded-lg border focus:ring-2 focus:ring-orange-500 outline-none ${
                errors.name ? 'border-red-500' : ''
              }`}
              type="text"
              placeholder="Nome completo"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors({ ...errors, name: null });
              }}
            />
            {errors.name && (
              <p className="auth-error-text text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <input
              className={`auth-input w-full p-3 rounded-lg border focus:ring-2 focus:ring-orange-500 outline-none ${
                errors.email ? 'border-red-500' : ''
              }`}
              type="email"
              placeholder="E-mail"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: null });
              }}
            />
            {errors.email && (
              <p className="auth-error-text text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <input
              className={`auth-input w-full p-3 rounded-lg border focus:ring-2 focus:ring-orange-500 outline-none ${
                errors.password ? 'border-red-500' : ''
              }`}
              type="password"
              placeholder="Senha"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors({ ...errors, password: null });
              }}
            />
            {errors.password && (
              <p className="auth-error-text text-red-500 text-sm mt-1">{errors.password}</p>
            )}
            <p className="auth-helper-text text-xs text-gray-500 mt-1">
              A senha deve conter no mínimo 8 caracteres, incluindo maiúsculas, minúsculas, números e caracteres especiais.
            </p>
          </div>

          <div>
            <input
              className={`auth-input w-full p-3 rounded-lg border focus:ring-2 focus:ring-orange-500 outline-none ${
                errors.confirmPassword ? 'border-red-500' : ''
              }`}
              type="password"
              placeholder="Confirmar senha"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: null });
              }}
            />
            {errors.confirmPassword && (
              <p className="auth-error-text text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
            )}
          </div>

          <button
            type="submit"
            className="auth-submit-btn w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition"
            disabled={loading || isSubmitting}
          >
            {loading || isSubmitting ? 'Cadastrando...' : 'Cadastrar'}
          </button>
        </form>

        <p className="auth-footer-text text-center text-sm text-gray-600 mt-4">
          Já tem uma conta?{' '}
          <a href="/login" className="text-orange-500 hover:underline font-semibold">
            Faça login
          </a>
        </p>
      </div>
    </div>
  );
}


